import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, TrendingUp, FileText, Quote, ClipboardList, Send } from "lucide-react";
import { SERVICES } from "@/data/services";
import { getCaseStudy, getCaseStudies } from "@/data/caseStudies";
import { getRequirements } from "@/data/articles";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudyDetail() {
  const { slug, caseSlug: caseSlugParam } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const study = service ? getCaseStudy(slug, caseSlugParam) : null;

  useSeo({
    title: study ? `${study.title} — ${service.name} Case Study | Tri-Axis XYZ` : "Case study — Tri-Axis XYZ",
    description: study ? `${study.challenge}` : "CAD drafting case study",
    image: service ? `${API}/og/${service.slug}` : undefined,
  });

  if (!service || !study) return <Navigate to="/services" replace />;

  const related = getCaseStudies(slug).filter((c) => c.slug !== study.slug);
  const requirements = getRequirements(slug).slice(0, 4);

  return (
    <div data-testid="page-case-study-detail">
      {/* Breadcrumb */}
      <div className="border-b border-slate-200 bg-paper-dark/40">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-4 mono text-[11px] tracking-[0.16em] uppercase text-slate-500 flex flex-wrap items-center gap-2">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-slate-900">Services</Link>
          <span>/</span>
          <Link to={`/services/${slug}`} className="hover:text-slate-900 text-topo">{service.name}</Link>
          <span>/</span>
          <span className="text-emerald-700">Case study</span>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700 flex items-center gap-2">
              <Quote className="h-3 w-3" /> Case study · {service.name}
            </div>
            <h1 className="display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900">
              {study.title}
            </h1>
            <div className="mt-4 mono text-[11px] tracking-[0.14em] uppercase text-slate-500">
              Client · {study.client}
            </div>
            {study.metrics && study.metrics.length > 0 && (
              <ul className="mt-6 flex flex-wrap gap-2" data-testid="case-study-metrics">
                {study.metrics.map((m) => (
                  <li
                    key={m}
                    className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.14em] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-sm"
                  >
                    <TrendingUp className="h-3 w-3" /> {m}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-slate-200 p-6 bg-paper-dark/40/70">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700">Deliverables shipped</div>
              <ul className="mt-4 space-y-2">
                {service.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-700 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* Brief + Outcome */}
      <section className="border-b border-slate-200 bg-paper-dark/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">The brief</div>
            <h2 className="display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-slate-900">
              What the client needed.
            </h2>
            <div className="mt-5 inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
              <FileText className="h-3.5 w-3.5" /> ~2 min read
            </div>
          </div>
          <div className="lg:col-span-8 text-slate-700 space-y-5 leading-relaxed">
            <p data-testid="case-study-challenge" className="text-base sm:text-lg">{study.challenge}</p>
            <div className="border-l-2 border-emerald-700 pl-5 bg-white p-5 rounded-sm">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-emerald-700">Outcome</div>
              <p data-testid="case-study-outcome" className="mt-2 text-base text-slate-700">{study.outcome}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our 3-step approach (templated per-service) */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">How we produced the deliverable</div>
          <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900">
            Our 3-step <span className="text-emerald-700">{service.name.toLowerCase()}</span> workflow.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="case-study-process">
            <ProcessStep
              n="01"
              title="Intake & QC of inputs"
              body={`Our senior CAD lead reviewed the client's submission against our ${service.name} intake checklist — flagged missing items, confirmed CAD standards, and locked the project scope before drafting began.`}
            />
            <ProcessStep
              n="02"
              title="Production drafting"
              body={`Our drafters produced the deliverable to the client's layer standards, blocks, and title-block template. Every measurement, label, and call-out was cross-checked against the source field data and recorded instruments.`}
            />
            <ProcessStep
              n="03"
              title="3-tier QC and sealing"
              body={`Senior drafter → CAD lead → licensed surveyor — three independent reviews before the deliverable was sealed and released. Closure, dimensions, and certificate language verified.`}
            />
          </div>
        </div>
      </section>

      {/* Inputs we relied on */}
      <section className="border-b border-slate-200 bg-paper-dark/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Inputs we relied on</div>
              <h2 className="display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-slate-900">
                What the client provided.
              </h2>
            </div>
            <p className="lg:col-span-5 text-slate-600">
              The same set of inputs we ask every {service.name.toLowerCase()} client to send. Missing items get flagged before production starts.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200" data-testid="case-study-inputs">
            {requirements.map((r) => (
              <li key={r.t} className="bg-white p-6">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center bg-orange-survey text-white rounded-sm shrink-0">
                    <ClipboardList className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="display text-base font-extrabold tracking-tight text-slate-900">{r.t}</div>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{r.d}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-slate-200 bg-cyanotype blueprint-grid text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-cream">Have a similar project?</div>
            <h2 className="display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
              Send the brief — we'll respond in <span className="text-emerald-400">under 24 hours</span>.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link
              to={`/services/${slug}#quote`}
              data-testid="case-study-quote-cta"
              className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-5 py-3"
            >
              <Send className="h-4 w-4" /> Request a quote
            </Link>
            <Link
              to={`/services/${slug}`}
              data-testid="case-study-back-to-service"
              className="inline-flex items-center gap-2 rounded-sm border border-slate-700 hover:border-white text-white text-sm font-semibold px-5 py-3"
            >
              <ArrowLeft className="h-4 w-4" /> Back to {service.name}
            </Link>
          </div>
        </div>
      </section>

      {/* Related case studies */}
      {related.length > 0 && (
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">More {service.name} case studies</div>
            <h3 className="display mt-3 text-2xl sm:text-3xl font-extrabold tracking-tighter text-slate-900">
              Related work in {service.name.toLowerCase()}.
            </h3>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {related.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/services/${slug}/case-studies/${c.slug}`}
                  data-testid={`related-case-${i}`}
                  className="bg-white p-6 group block"
                >
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Case study</div>
                  <div className="display mt-2 text-lg font-extrabold tracking-tight text-slate-900">{c.title}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{c.outcome}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-topo">
                    Read full case study <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function ProcessStep({ n, title, body }) {
  return (
    <div className="bg-white p-6 lg:p-7">
      <div className="display text-4xl font-extrabold tracking-tighter text-topo">{n}</div>
      <div className="display mt-3 text-lg font-extrabold tracking-tight text-slate-900">{title}</div>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
